/**
 * Mouvements de trésorerie qui impactent le solde
 * mais ne sont PAS des revenus/dépenses de budget.
 */

export type TransactionType = 'depense' | 'revenu' | 'transfert' | 'pret' | 'remboursement';

const OPERATING_TYPES = new Set(['depense', 'revenu']);

export const NEUTRAL_CASHFLOW_TYPES = new Set(['transfert', 'pret', 'remboursement']);

function normalizeCategoryName(nom?: string | null): string {
  return (nom || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function isHealthReimbursementCategory(nom?: string | null): boolean {
  const n = normalizeCategoryName(nom);
  if (!n.includes('remboursement') && !n.includes('rembours')) return false;
  return n.includes('sant') || n.includes('mutuelle') || n.includes('assurance');
}

export function isLoanOutCategory(nom?: string | null): boolean {
  const n = normalizeCategoryName(nom);
  if (!n) return false;
  if (n === 'pret' || n === 'prets') return true;
  if (n.startsWith('pret ') || n.startsWith('prets ')) return true;
  if (n.includes('pret entre') || n.includes('pret ami')) return true;
  return false;
}

export function isLoanRepaymentCategory(nom?: string | null): boolean {
  const n = normalizeCategoryName(nom);
  if (!n) return false;
  if (isHealthReimbursementCategory(nom)) return false;
  if (n.includes('remboursement') || n.includes('rembours')) return true;
  return false;
}

export function isTransferRelatedCategory(nom?: string | null): boolean {
  const n = normalizeCategoryName(nom);
  return n.includes('transfert') || n.includes('virement');
}

export function isLoanRelatedCategory(nom?: string | null): boolean {
  return isLoanOutCategory(nom) || isLoanRepaymentCategory(nom);
}

export function countsAsOperatingIncomeOrExpense(
  type: string | null | undefined,
  categoryNom?: string | null,
): boolean {
  if (!type) return false;
  if (NEUTRAL_CASHFLOW_TYPES.has(type)) return false;
  if (!OPERATING_TYPES.has(type)) return false;
  if (isLoanRelatedCategory(categoryNom)) return false;
  return true;
}

type CategoryLike = { id?: string; nom: string; type?: string | null };

export function filterCategoriesForTransactionType<T extends CategoryLike>(
  categories: T[],
  txType: string | null | undefined,
  options?: { keepCategoryId?: string | null },
): T[] {
  const keepId = options?.keepCategoryId || null;

  let filtered: T[];
  switch (txType) {
    case 'depense':
      filtered = categories.filter(
        (c) =>
          c.type === 'depense' &&
          !isLoanOutCategory(c.nom) &&
          !isTransferRelatedCategory(c.nom),
      );
      break;
    case 'revenu':
      filtered = categories.filter(
        (c) =>
          c.type === 'revenu' &&
          !isLoanRepaymentCategory(c.nom) &&
          !isTransferRelatedCategory(c.nom),
      );
      break;
    case 'pret': {
      const preferred = categories.filter(
        (c) => (c.type === 'depense' || !c.type) && isLoanOutCategory(c.nom),
      );
      filtered =
        preferred.length > 0
          ? preferred
          : categories.filter((c) => c.type === 'depense' && !isTransferRelatedCategory(c.nom));
      break;
    }
    case 'remboursement': {
      const preferred = categories.filter(
        (c) => (c.type === 'revenu' || !c.type) && isLoanRepaymentCategory(c.nom),
      );
      filtered =
        preferred.length > 0
          ? preferred
          : categories.filter(
              (c) =>
                c.type === 'revenu' &&
                !isHealthReimbursementCategory(c.nom) &&
                !isTransferRelatedCategory(c.nom),
            );
      break;
    }
    case 'transfert': {
      const preferred = categories.filter((c) => isTransferRelatedCategory(c.nom));
      filtered = preferred.length > 0 ? preferred : [...categories];
      break;
    }
    default:
      filtered = [...categories];
  }

  if (keepId && !filtered.some((c) => c.id === keepId)) {
    const current = categories.find((c) => c.id === keepId);
    if (current) filtered = [current, ...filtered];
  }

  return filtered;
}

export function transactionTypeLabel(type: string): string {
  switch (type) {
    case 'depense':
      return 'Dépense';
    case 'revenu':
      return 'Revenu';
    case 'transfert':
      return 'Transfert';
    case 'pret':
      return 'Prêt (sortie)';
    case 'remboursement':
      return 'Remboursement reçu';
    default:
      return type;
  }
}
